import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { LinkColor, LinkUnderline, QueryParams } from '@freelancer/ui/link';
import {
  FontColor,
  FontType,
  FontWeight,
  TextAlign,
  TextSize,
  TextTransform,
} from '@freelancer/ui/text';
import { IconColor, IconSize } from '../icon';
import { IconConfig, TagColor, TagSize, TagType } from './tag.types';

@Component({
  selector: 'fl-tag',
  template: `
    <fl-link
      *ngIf="link"
      class="Tag"
      [attr.data-tag-borderless]="borderless"
      [attr.data-tag-color]="color"
      [attr.data-tag-dark-mode]="darkMode"
      [attr.data-tag-type]="attrType"
      [attr.data-tag-size]="size"
      [attr.data-tag-size-tablet]="sizeTablet"
      [attr.data-tag-size-desktop]="sizeDesktop"
      [attr.data-tag-size-desktop-large]="sizeDesktopLarge"
      [attr.data-tag-size-desktop-xlarge]="sizeDesktopXLarge"
      [attr.data-tag-size-desktop-xxlarge]="sizeDesktopXXLarge"
      [attr.data-tag-invalid]="isInvalid"
      [color]="borderless === true ? LinkColor.DEFAULT : LinkColor.INHERIT"
      [link]="link"
      [newTab]="newTab"
      [queryParams]="queryParams"
      [underline]="LinkUnderline.NEVER"
    >
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </fl-link>

    <div
      class="Tag"
      *ngIf="!link"
      [class.IsSelected]="selected"
      [attr.data-tag-color]="color"
      [attr.data-tag-invalid]="isInvalid"
      [attr.data-tag-borderless]="borderless"
      [attr.data-tag-dark-mode]="darkMode"
      [attr.data-tag-type]="attrType"
      [attr.data-tag-size]="size"
      [attr.data-tag-size-tablet]="sizeTablet"
      [attr.data-tag-size-desktop]="sizeDesktop"
      [attr.data-tag-size-desktop-large]="sizeDesktopLarge"
      [attr.data-tag-size-desktop-xlarge]="sizeDesktopXLarge"
      [attr.data-tag-size-desktop-xxlarge]="sizeDesktopXXLarge"
    >
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </div>

    <ng-template #content>
      <div
        class="ContentTextSizeSetterContainer"
        [attr.data-tag-size]="size"
        [attr.data-tag-size-tablet]="sizeTablet"
        [attr.data-tag-size-desktop]="sizeDesktop"
        [attr.data-tag-size-desktop-large]="sizeDesktopLarge"
        [attr.data-tag-size-desktop-xlarge]="sizeDesktopXLarge"
        [attr.data-tag-size-desktop-xxlarge]="sizeDesktopXXLarge"
      >
        <fl-icon
          *ngIf="iconConfig"
          [name]="iconConfig.name"
          [size]="iconConfig.size"
          [color]="iconConfig?.color"
          [useIconFont]="!!iconConfig.useIconFont"
          [label]="iconConfig.label"
          [flMarginRight]="'xxxsmall'"
        ></fl-icon>
        <fl-text
          [fontType]="FontType.SPAN"
          [color]="isInvalid ? FontColor.ERROR : FontColor.INHERIT"
          [maxLines]="maxLines"
          [maxHeight]="TextSize.INHERIT"
          [textAlign]="textAlign"
          [textTransform]="textTransform"
          [size]="TextSize.INHERIT"
          [weight]="fontWeight"
          [flMarginRight]="
            attrType === TagType.DISMISSABLE ? 'xxxsmall' : 'none'
          "
        >
          <div class="Content">
            <ng-content></ng-content>
          </div>
        </fl-text>
      </div>
      <fl-icon
        *ngIf="attrType === TagType.DISMISSABLE"
        i18n-label="Remove tag button"
        label="Remove"
        class="CloseIcon"
        [name]="'ui-close'"
        [color]="IconColor.INHERIT"
        [size]="IconSize.SMALL"
        (click)="handleDismiss($event)"
      ></fl-icon>
    </ng-template>
  `,
  styleUrls: ['./tag.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagComponent implements OnChanges {
  FontColor = FontColor;
  TextSize = TextSize;
  FontType = FontType;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkUnderline = LinkUnderline;
  TagSize = TagSize;
  TagType = TagType;

  /**
   * Used for styling purposes only so we don't need to
   * override the [type] input when [link] is provided
   */
  attrType: TagType;

  @Input() color = TagColor.LIGHT;
  @Input() link?: string;
  @Input() newTab?: boolean;
  @Input() queryParams?: QueryParams;
  @Input() isInvalid?: boolean;
  @Input() type = TagType.DEFAULT;
  @Input() size = TagSize.MID;
  @Input() sizeTablet?: TagSize;
  @Input() sizeDesktop?: TagSize;
  @Input() sizeDesktopLarge?: TagSize;
  @Input() sizeDesktopXLarge?: TagSize;
  @Input() sizeDesktopXXLarge?: TagSize;
  @Input() selected = false;
  // TODO: T298874 - Max lines is broken for small and large tags
  @Input() maxLines = 1;
  @Input() textAlign?: TextAlign;
  @Input() textTransform?: TextTransform;
  @Input() fontWeight: FontWeight = FontWeight.NORMAL;
  @Input() iconConfig?: IconConfig;

  @HostBinding('attr.data-tag-borderless')
  @Input()
  borderless = false;

  @HostBinding('attr.data-tag-dark-mode')
  @Input()
  darkMode = false;

  @Output() dismiss = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (this.link) {
      this.attrType = TagType.CLICKABLE;
    } else {
      this.attrType = this.type;
    }
  }

  handleDismiss(event: Event): void {
    event.stopPropagation();
    this.dismiss.next();
  }
}
